import { HttpErrorResponse } from '@angular/common/http';
import { Injector } from '@angular/core';
import { AuthService } from '@abp/ng.core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { Observable, of } from 'rxjs';

/**
 * The message the guest paths return when the PIN or keyring password does not match. The PIN
 * component already shows this next to the input as a localized label, so a modal saying the same
 * thing in the server's English is noise on top of it.
 */
const REJECTED_CREDENTIAL_MESSAGE = "pin or keyring password isn't correct";

/**
 * ABP runs this before its own error handling, feeding whatever this returns into a switchMap
 * whose subscriber has no error callback - so this must always emit (of(...)), never error
 * (throwError), or it permanently kills ABP's error-reporting subscription for the rest of the
 * session. Returning of(null) means "handled" and nothing further is shown; returning of(error)
 * falls back to ABP's default - a modal carrying the server's message when the response has ABP's
 * error envelope, and the full-page error screen when it does not.
 */
export function appHttpErrorHandler(injector: Injector, error: unknown): Observable<unknown> {
  if (error instanceof HttpErrorResponse) {
    // Shown inline by the PIN field itself.
    if (isRejectedCredential(error)) {
      return of(null);
    }

    // A guest session refused an operation. The error screen is the wrong answer: it throws away
    // the asset the operator was working on, so it reads like the app broke rather than a refusal.
    if (isGuestRefusal(injector, error)) {
      injector.get(ConfirmationService).error(
        {
          key: 'AbpExceptionHandling::DefaultErrorMessage403Detail',
          defaultValue: 'You are not allowed to perform this operation.',
        },
        {
          key: 'AbpExceptionHandling::DefaultErrorMessage403',
          defaultValue: 'You are not authorized!',
        },
        { hideCancelBtn: true, yesText: 'AbpAccount::Close' }
      );

      return of(null);
    }
  }

  return of(error);
}

function isRejectedCredential(error: HttpErrorResponse): boolean {
  const message = error.error?.error?.message;

  return (
    typeof message === 'string' &&
    message.trim().toLowerCase() === REJECTED_CREDENTIAL_MESSAGE
  );
}

function isGuestRefusal(injector: Injector, error: HttpErrorResponse): boolean {
  if (error.status !== 401 && error.status !== 403) {
    return false;
  }

  // A response carrying ABP's error envelope already has a message worth reading - a restricted
  // asset, an invalid location - and ABP shows it in a modal itself. Only a bare authorization
  // failure arrives without one (measured: no _AbpErrorFormat header, empty body), and that is the
  // case that otherwise takes over the page. Handling both would put our generic "not allowed" on
  // top of the server's specific message.
  if (error.headers.has('_AbpErrorFormat')) {
    return false;
  }

  // Only for a guest session - the same test the app uses to decide a page is temporary. A signed-in
  // user's bodiless 401 means the opposite, a dead session, and that still belongs on the error
  // screen.
  //
  // Both 401 and 403 count: the kiosk paths refuse with 401 because the impersonated principal ABP
  // builds from a PIN carries no authentication type, so ICurrentUser.IsAuthenticated is false and
  // ABP maps the failure to 401 rather than 403. Covering both keeps this working either way.
  return !injector.get(AuthService).isAuthenticated;
}
